import React, { useEffect, useRef, useState } from 'react';

import Text from '~components/atoms/Text';

import TabPageContainer from '~components/atoms/TabPageContainer';
import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';

import * as Styles from './_reports.module.scss';
import DownloadCSV from '~components/molecules/DownloadCSV';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { FundTypeEnum, GoldSilverTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum, TransactionActionEnum, TransactionStatusEnum } from '~utils/enums';
import dayjs from 'dayjs';

import UtilFunctions from '~utils/functions';
import combinedActions from '~redux/combinedActions';
import SalesReportSearchAndFilterBar from './SalesReportSearchAndFilterBar';
import SalesReportCardsSummary from './SalesReportCardsSummary';
import SalesReportTable from './SalesReportTable';
import SalesReportTablePaginator from './SalesReportTablePaginator';
import Decimal from 'decimal.js';

const SalesReport = () => {
    const dispatch = useAppDispatch();
    const {
        Misc: {
            dateTimeFormat,
        },
    } = UtilFunctions;

    const isReadyToExportRef = useRef(false);

    const salesReportListingForExport = useAppSelector(state => state.report.reportExportSalesReportListingForExport);
    const geTExportSalesReportListingForExportIsLoading = useAppSelector(state => state.report.loadings.exporTExportSalesReportListing);
    const salesReportListingIndex = useAppSelector(state => state.report.salesReport.salesReportTransactionListing.index);

    const { totalSales, totalTransactions, totalAffinSplit, totalTTTSplit } = useAppSelector((state) => state.report.salesReport.salesReportCardSummary);

    const exporTExportSalesReportListing = () => {
        isReadyToExportRef.current = true; 
        dispatch(combinedActions.reportGetSalesReportExportAttempt());
    };
    const clearSalesReportListingForExport = () => dispatch(combinedActions.reportClearSalesReportListingForExport());

    const [isExportingSalesReportListing, setIsExportingSalesReportListing] = useState(false);

    useEffect(() => {
        dispatch(combinedActions.reportGetSalesReportTransactionListingAttempt());
        dispatch(combinedActions.reportGetSalesReportCardSummaryAttempt());
    }, [salesReportListingIndex]);

    const listHeader = [
        'TRANSACTION ID',
        'TRANSACTION ITEM ID',
        'TRANSACTION TYPE',
        'BRANCH',
        'SERIAL NUMBER',
        'PRODUCT NAME',
        'STATUS',
        'GOLD TYPE',
        'SILVER TYPE',
        'TOTAL NET VALUE (RM)',
        'TOTAL NET VALUE PER ITEM (RM)',
        'TOTAL WEIGHT (G)',
        'DESCRIPTION',
        'TOTAL WEIGHT PER UNIT (G)',
        'API BASED PRICE (RM)',
        'DISCOUNT NAME',
        'DISCOUNT CODE',
        'DISCOUNT TYPE',
        'DISCOUNT RATE (%)',
        'FLAT DISCOUNT RATE (RM)',
        'AFFIN SPLIT PER UNIT (RM)',
        'AFFIN SPLIT RATE PER UNIT (%)',
        'AFFIN SPLIT RATE PER UNIT AFTER DISCOUNT (RM)',
        'AFFIN SPLIT RATE PER UNIT AFTER DISCOUNT (%)',
        'MINTING COST PER UNIT',
        'MINTING RATE PER UNIT',
        'TTT SPLIT PER UNIT (RM)',
        'TRANSACTION DATE',
    ];

    const listData = salesReportListingForExport.map(transaction => {
        const {
            transactionId,
            transactionItemId,
            transactionType,
            branchName,
            serialNumbers,
            productName,
            status,
            goldType,
            silverType,
            totalNetValue,
            netValuePerTransactionItem,
            totalWeight,
            description,
            totalWeightPerUnit,
            apiBasedPrice,
            discountName,
            discountCode,
            discountTransactionType,
            discountRatePercentage,
            flatDiscountRate,
            affinSplitPerUnit,
            affinSplitRatePerUnit,
            affinSplitPerUnitAfterDiscount,
            affinSplitRatePerUnitAfterDiscount,
            mintingCostPerUnit,
            mintingRatePerUnit,
            tttSplitPerUnit,
            transactionDate,
        } = transaction;

        const massagedSerialNumbers = (serialNumbers || [])
            .filter(item => item)
            .join('|');

        const getDiscountTransactionType = (discountType: TransactionActionEnum) => {
            if (discountType === TransactionActionEnum['Bank Buy']) return 'MARKUP';
            if (discountType === TransactionActionEnum['Bank Sell']) return 'DISCOUNT';

            return '-';
        };

        return {
            transactionId: transactionId || '-',
            transactionItemId: transactionItemId || '-',
            transactionType: TransactionActionEnum[transactionType] || '-',
            branchName: branchName || '-',
            serialNumbers: massagedSerialNumbers.length > 0 ? massagedSerialNumbers : '-',
            productName: productName || '-',
            status: TransactionStatusEnum[status] || '-',
            goldType: GoldTypeEnum[goldType]
                ? GoldTypeEnum[goldType].split(/(?=[A-Z])/).join(' ')
                : '-',
            silverType: SilverTypeEnum[silverType]
                ? SilverTypeEnum[silverType].split(/(?=[A-Z])/).join(' ')
                : '-',
            totalNetValue: totalNetValue !== null && totalNetValue !== undefined
                ? Decimal(totalNetValue).toFixed(2)
                : '-',
            netValuePerTransactionItem: netValuePerTransactionItem !== null && netValuePerTransactionItem !== undefined
                ? Decimal(netValuePerTransactionItem).toFixed(2)
                : '-', 
            totalWeight: totalWeight !== null && totalWeight !== undefined
                ? Decimal(totalWeight).toFixed(3)
                : '-',
            description: description || '-',
            totalWeightPerUnit: totalWeightPerUnit !== null && totalWeightPerUnit !== undefined
                ? Decimal(totalWeightPerUnit).toFixed(3)
                : '-',
            apiBasedGoldPrice: apiBasedPrice !== null && apiBasedPrice !== undefined
                ? Decimal(apiBasedPrice).toFixed(2)
                : '-',
            discountName: discountName || '-',
            discountCode: discountCode || '-',
            discountTransactionType: getDiscountTransactionType(discountTransactionType) || '-',
            discountRatePercentage: discountRatePercentage !== null && discountRatePercentage !== undefined
                ? Decimal(discountRatePercentage).toFixed(2)
                : '-',
            flatDiscountRate: flatDiscountRate !== null && flatDiscountRate !== undefined
                ? Decimal(flatDiscountRate).toFixed(2) 
                : '-',
            affinSplitPerUnit: affinSplitPerUnit !== null && affinSplitPerUnit !== undefined 
                ? Decimal(affinSplitPerUnit).toFixed(2) 
                : '-',
            affinSplitRatePerUnit: affinSplitRatePerUnit !== null && affinSplitRatePerUnit !== undefined
                ? Decimal(affinSplitRatePerUnit).toFixed(2)
                : '-',
            affinSplitPerUnitAfterDiscount: affinSplitPerUnitAfterDiscount !== null && affinSplitPerUnitAfterDiscount !== undefined
                ? Decimal(affinSplitPerUnitAfterDiscount).toFixed(2) 
                : '-',
            affinSplitRatePerUnitAfterDiscount: affinSplitRatePerUnitAfterDiscount !== null && affinSplitRatePerUnitAfterDiscount !== undefined 
                ? Decimal(affinSplitRatePerUnitAfterDiscount).toFixed(2)
                : '-',
            mintingCostPerUnit: mintingCostPerUnit !== null && mintingCostPerUnit !== undefined
                ? Decimal(mintingCostPerUnit).toFixed(2)
                : '-',
            mintingRatePerUnit: mintingRatePerUnit !== null && mintingRatePerUnit !== undefined
                ? Decimal(mintingRatePerUnit).toFixed(2)
                : '-',
            tttSplitPerUnit: tttSplitPerUnit !== null && tttSplitPerUnit !== undefined
                ? Decimal(tttSplitPerUnit).toFixed(2)
                : '-',
            transactionDate: dateTimeFormat(transactionDate) || '-',
        };
    });

    const handleExportGoldTransactionListingCleanup = () => {
        clearSalesReportListingForExport();
        setIsExportingSalesReportListing(false);
        isReadyToExportRef.current = false;
    };

    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Sales Report'
                    className={Styles.title}
                    isBold
                />

                <div className={Styles['action-container']}>
                    <DownloadCSV
                        exportTestId='salesReport-transaction-export-button'
                        buttonLabel='Export Sales Transaction to CSV'
                        header={listHeader}
                        data={listData}
                        fileName={dayjs().format('DD-MM-YYYY').concat(' Sales Transaction Report')}
                        onClick={exporTExportSalesReportListing}
                        cleanup={handleExportGoldTransactionListingCleanup}
                        isLoading={geTExportSalesReportListingForExportIsLoading || isExportingSalesReportListing}
                    />
                </div>
            </TabPageHeaderWrapper>

            <SalesReportSearchAndFilterBar />

            <SalesReportCardsSummary
                totalSales={totalSales}
                totalTransactions={totalTransactions}
                totalAffinSplit={totalAffinSplit}
                totalTttSplit={totalTTTSplit}
            />

            <SalesReportTable />

            <SalesReportTablePaginator />
        </TabPageContainer>
    );
};

export default SalesReport;
